import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Box } from '@mui/material';
import { defaultNodeCategories } from 'src/utils/knowlege-graph-api';

const KnowledgeGraph = ({ data, config, onNodeClick, onNodeHover, nodeCategories }) => {
  console.log('Rendering KnowledgeGraph with data:', data);
  const graphRef = useRef();
  const hoveredNodeRef = useRef(null);
  
  // Use passed nodeCategories or fallback to default
  const categories = nodeCategories || defaultNodeCategories;

  useEffect(() => {
    if (graphRef.current) {
      // Configure forces for organic layout
      graphRef.current.d3Force('charge').strength(-400);
      graphRef.current.d3Force('link').distance(link => {
        // Central nodes (user/room) connections should be longer
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        const sourceCategory = data.nodes.find(n => n.id === sourceId)?.category;
        const targetCategory = data.nodes.find(n => n.id === targetId)?.category;
        if (sourceCategory === 'user' || targetCategory === 'user' || 
            sourceCategory === 'room' || targetCategory === 'room') {
          return 150;
        }
        return 80;
      });
      graphRef.current.d3Force('center').strength(0.1);
    }
  }, [data.nodes]);

  // Memoize connected nodes for highlighting
  const getConnectedNodes = useMemo(() => {
    const connections = {};
    data.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      if (!connections[sourceId]) connections[sourceId] = new Set();
      if (!connections[targetId]) connections[targetId] = new Set();
      
      connections[sourceId].add(targetId);
      connections[targetId].add(sourceId);
    });
    return connections;
  }, [data.links]);

  const getNodeColor = useCallback((node) => {
    if (!node) return '#ffffff';
    const category = categories[node.category];
    return category ? category.color : '#ffffff';
  }, [categories]);

  const drawNode = useCallback((node, ctx, globalScale) => {
    // Safety check for valid coordinates
    if (!node || typeof node.x !== 'number' || typeof node.y !== 'number' || 
        !isFinite(node.x) || !isFinite(node.y)) {
      return;
    }

    const isCentral = node.category === 'user' || node.category === 'room';
    const isHovered = hoveredNodeRef.current === node.id;
    const isConnectedToHovered = hoveredNodeRef.current && 
      getConnectedNodes[hoveredNodeRef.current]?.has(node.id);
    
    const baseSize = (node.val || 10) * (config.nodeSize / 8);
    const size = isCentral ? baseSize * 1.3 : baseSize;
    const color = getNodeColor(node);
    
    // Fade unconnected nodes when hovering
    const alpha = hoveredNodeRef.current 
      ? (isHovered || isConnectedToHovered || node.id === hoveredNodeRef.current ? 1 : 0.15)
      : 1;
    
    ctx.globalAlpha = alpha;
    
    // Draw outer glow
    if (isCentral || isHovered) {
      const gradient = ctx.createRadialGradient(node.x, node.y, size * 0.5, node.x, node.y, size * 2.5);
      gradient.addColorStop(0, `${color}40`);
      gradient.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(node.x, node.y, size * 2.5, 0, 2 * Math.PI);
      ctx.fillStyle = gradient;
      ctx.fill();
    }
    
    // Draw pulse ring for central node
    if (isCentral) {
      const time = Date.now() * 0.002;
      const pulseSize = size * (1.5 + Math.sin(time) * 0.3);
      ctx.beginPath();
      ctx.arc(node.x, node.y, pulseSize, 0, 2 * Math.PI);
      ctx.strokeStyle = `${color}30`;
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }
    
    // Draw secondary ring
    ctx.beginPath();
    ctx.arc(node.x, node.y, size * 1.2, 0, 2 * Math.PI);
    ctx.strokeStyle = `${color}50`;
    ctx.lineWidth = 1.5 / globalScale;
    ctx.stroke();
    
    // Draw main node
    ctx.beginPath();
    ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
    
    // Create gradient fill
    const nodeGradient = ctx.createRadialGradient(
      node.x - size * 0.3, node.y - size * 0.3, 0,
      node.x, node.y, size
    );
    nodeGradient.addColorStop(0, color);
    nodeGradient.addColorStop(1, `${color}80`);
    ctx.fillStyle = nodeGradient;
    ctx.fill();
    
    // Draw label
    const label = node.name || node.id;
    const fontSize = Math.max(10 / globalScale, isCentral ? 14 / globalScale : 11 / globalScale);
    ctx.font = `${isCentral ? 600 : 500} ${fontSize}px 'Outfit', sans-serif`;
    
    const labelY = node.y + size + fontSize * 1.2;
    
    // Label background
    const textWidth = ctx.measureText(label).width;
    const padding = fontSize * 0.4;
    
    ctx.fillStyle = 'rgba(3, 0, 20, 0.85)';
    ctx.beginPath();
    
    // Use roundRect if available, otherwise fallback to fillRect
    if (ctx.roundRect) {
      ctx.roundRect(
        node.x - textWidth / 2 - padding,
        labelY - fontSize / 2 - padding / 2,
        textWidth + padding * 2,
        fontSize + padding,
        fontSize * 0.3
      );
    } else {
      ctx.rect(
        node.x - textWidth / 2 - padding,
        labelY - fontSize / 2 - padding / 2,
        textWidth + padding * 2,
        fontSize + padding
      );
    }
    ctx.fill();
    
    // Label text
    ctx.fillStyle = isCentral ? color : '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, node.x, labelY);
    
    ctx.globalAlpha = 1;
  }, [config.nodeSize, getNodeColor, getConnectedNodes]);

  const drawLink = useCallback((link, ctx) => {
    // Safety check for valid coordinates
    if (!link.source || !link.target) return;
    
    const sourceX = link.source.x;
    const sourceY = link.source.y;
    const targetX = link.target.x;
    const targetY = link.target.y;
    
    // Check if all coordinates are valid numbers
    if (!isFinite(sourceX) || !isFinite(sourceY) || 
        !isFinite(targetX) || !isFinite(targetY)) {
      return;
    }
    
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;
    
    const isConnectedToHovered = hoveredNodeRef.current && 
      (sourceId === hoveredNodeRef.current || targetId === hoveredNodeRef.current);
    
    const alpha = hoveredNodeRef.current 
      ? (isConnectedToHovered ? 0.6 : 0.05)
      : 0.2;
    
    const sourceColor = getNodeColor(link.source);
    const targetColor = getNodeColor(link.target);
    
    // Create gradient for link
    const gradient = ctx.createLinearGradient(sourceX, sourceY, targetX, targetY);
    const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
    gradient.addColorStop(0, `${sourceColor}${alphaHex}`);
    gradient.addColorStop(1, `${targetColor}${alphaHex}`);
    
    ctx.beginPath();
    ctx.moveTo(sourceX, sourceY);
    ctx.lineTo(targetX, targetY);
    ctx.strokeStyle = gradient;
    ctx.lineWidth = isConnectedToHovered ? config.linkWidth * 1.5 : config.linkWidth;
    ctx.stroke();
  }, [config.linkWidth, getNodeColor]);

  const handleNodeHover = useCallback((node) => {
    hoveredNodeRef.current = node ? node.id : null;
    if (onNodeHover) onNodeHover(node);
    
    // Change cursor
    document.body.style.cursor = node ? 'pointer' : 'default';
  }, [onNodeHover]);

  const handleClick = useCallback((node) => {
    if (node && onNodeClick) {
      onNodeClick(node);
      
      // Zoom to clicked node
      if (graphRef.current && isFinite(node.x) && isFinite(node.y)) {
        graphRef.current.centerAt(node.x, node.y, 500);
        graphRef.current.zoom(2, 500);
      }
    }
  }, [onNodeClick]);

  // Handle empty data
  if (!data || !data.nodes || data.nodes.length === 0) {
    return (
      <Box 
        sx={{ 
          width: '100%', 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '1rem',
        }}
      >
        No data to display. Enter an ID and click "Load Graph".
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%',
        '& canvas': {
          outline: 'none',
        }
      }}
    >
      <ForceGraph2D
        ref={graphRef}
        graphData={data}
        nodeLabel=""
        nodeCanvasObject={drawNode}
        nodeCanvasObjectMode={() => 'replace'}
        linkCanvasObject={drawLink}
        linkCanvasObjectMode={() => 'replace'}
        linkDirectionalParticles={config.particlesEnabled ? 2 : 0}
        linkDirectionalParticleWidth={3}
        linkDirectionalParticleSpeed={0.005}
        linkDirectionalParticleColor={(link) => link.source ? getNodeColor(link.source) : '#ffffff'}
        onNodeClick={handleClick}
        onNodeHover={handleNodeHover}
        backgroundColor="transparent"
        cooldownTicks={100}
        onEngineStop={() => graphRef.current?.zoomToFit(400, 80)}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        minZoom={0.3}
        maxZoom={5}
      />
    </Box>
  );
};

export default KnowledgeGraph;
